/**
 * @file     AlertSignupFlow.tsx
 * @purpose  Alert signup for the programmatic pages, in four presentations. `full` is the three-step card: the visitor picks what to watch and how often before being asked for anything. `aside`, `band` and `compact` start as the kit's one-row email capture, subscribing to the page's own intent, then ask for first name, last name and mobile phone before sending. Every variant collects all four (contactFields contract). Every variant is double opt-in: success means a confirmation email went out, never that alerts are on. No LLM (ADR-009).
 */
'use client';

import { useId, useState, type ChangeEvent, type FormEvent, type ReactNode } from 'react';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import { useTranslation } from 'react-i18next';
import { useAnalytics } from '@/hooks/useAnalytics';
import { submitLead } from '@/services/leads/submitLead';
import { useAntiSpam } from '@/services/anti-spam';
import { AntiSpamHoneypot } from '@/components/forms/AntiSpamHoneypot';
import { ContactFields } from '@/components/lead-capture/ContactFields';
import { FormConsentLine } from '@/components/lead-capture/FormConsentLine';
import { ConsentChoices, useConsentChoices } from '@/components/lead-capture/ConsentChoices';
import {
  EMPTY_CONTACT,
  contactPayload,
  validateContact,
  type ContactErrors,
  type ContactField,
  type ContactValues,
} from '@/services/leads/contactFields';
import { secureFetch } from '@/services/anti-scraping/secureFetch';
import { FlowStepHeader } from '@/features/lead-flows/components/FlowStepHeader';
import { StepTransition } from '@/components/animation/StepTransition';
import {
  flowColors,
  flowFocusRing,
  flowPrimaryButtonSx,
  flowRadius,
  flowTransition,
  reducedMotion,
} from '@/features/lead-flows/components/flowKitTokens';
import type { ShortlistIntent, ShortlistAlertEvent } from './shortlistIntent';

export type AlertSignupVariant = 'full' | 'aside' | 'band' | 'compact';

export type AlertSignupPerk = { title: string; body: string };

interface Props {
  intent: ShortlistIntent;
  locationTag: string;
  /** Place name used in the copy, e.g. "Vancouver". */
  place: string;
  /**
   * Fired once the subscription is actually accepted. Callers that show this
   * in a modal use it to mark the surface done, so a visitor who opens and
   * dismisses the form is not treated as having subscribed.
   */
  onSent?: () => void;
  /** Defaults to the three-step `full` card. */
  variant?: AlertSignupVariant;
  /** One-row variants: the heading. Defaults to "Get new {place} listings by email". */
  title?: string;
  /** One-row variants: one supporting line under the heading. */
  sub?: string;
  /** One-row variants: a small label above the heading. */
  eyebrow?: string;
  /** One-row variants: the button. */
  cta?: string;
  /** Band variant: short benefit pairs under the form. */
  perks?: AlertSignupPerk[];
  /** Heading level. Defaults to h3, or h2 for the band (a page section). */
  headingComponent?: 'h2' | 'h3';
  /** Band variant: content rendered after the perks (a secondary link). */
  footer?: ReactNode;
  /** Added to alert_signup_started as `surface` (e.g. 'rentals_hub'). Add-only property. */
  analyticsSurface?: string;
}

const WATCH_OPTIONS: { key: ShortlistAlertEvent; label: string }[] = [
  { key: 'new_listing', label: 'New listings' },
  { key: 'price_drop', label: 'Price drops' },
  { key: 'open_house', label: 'Open houses' },
];

const CADENCE: { key: 'instant' | 'weekly'; label: string }[] = [
  { key: 'instant', label: 'As they happen' },
  { key: 'weekly', label: 'Once a week' },
];

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
export const ALERT_EMAIL_ERROR = 'Enter an email address, like name@example.com.';
type State = 'idle' | 'sending' | 'sent' | 'error';

const BellIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" />
    <path d="M10.3 21a1.9 1.9 0 0 0 3.4 0" />
  </svg>
);

const MailIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ flexShrink: 0 }}>
    <rect x="3" y="5" width="18" height="14" rx="2" />
    <path d="M3 7l9 6 9-6" />
  </svg>
);

const CheckIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M5 12.5l4.5 4.5L19 7.5" />
  </svg>
);

const contactFieldSx = { '& .MuiOutlinedInput-root': { borderRadius: `${flowRadius.md}px` } } as const;

const optionTileSx = (on: boolean) => ({
  p: 2,
  minHeight: 44,
  cursor: 'pointer',
  borderRadius: `${flowRadius.md}px`,
  border: `1px solid ${on ? flowColors.interactivePrimary : flowColors.borderStrong}`,
  boxShadow: on ? `inset 0 0 0 1px ${flowColors.interactivePrimary}` : 'none',
  backgroundColor: on ? flowColors.chip : flowColors.surfaceCard,
  transition: flowTransition('border-color', 'background-color', 'box-shadow'),
  '&:hover': { borderColor: flowColors.interactivePrimary },
  ...flowFocusRing,
  [reducedMotion]: { transition: 'none' },
});

export function AlertSignupFlow({
  intent,
  locationTag,
  place,
  onSent,
  variant = 'full',
  title,
  sub,
  eyebrow,
  cta,
  perks,
  headingComponent,
  footer,
  analyticsSurface,
}: Props) {
  const { trackCtaClick, trackEvent } = useAnalytics();
  const emailId = useId();
  const errorId = useId();
  const headingId = useId();

  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [watch, setWatch] = useState<ShortlistAlertEvent[]>(intent.alertOn);
  const [cadence, setCadence] = useState<'instant' | 'weekly'>(intent.frequency);
  const { t } = useTranslation();
  const [contact, setContact] = useState<ContactValues>(EMPTY_CONTACT);
  const [contactErrors, setContactErrors] = useState<ContactErrors>({});
  // One-row variants ask for the email first, then the rest of the contact.
  const [rowStep, setRowStep] = useState<'email' | 'contact'>('email');
  const [state, setState] = useState<State>('idle');
  const [error, setError] = useState<string | null>(null);
  const [emailError, setEmailError] = useState<string | null>(null);
  const consent = useConsentChoices();
  const { setHoneypotValue, getAntiSpamMetadata } = useAntiSpam();
  const email = contact.email;
  const setEmail = (value: string) => setContact((c) => ({ ...c, email: value }));
  const onContactChange = (field: ContactField, value: string) => {
    setContact((c) => ({ ...c, [field]: value }));
    if (contactErrors[field]) setContactErrors((errs) => ({ ...errs, [field]: undefined }));
  };

  const isFull = variant === 'full';
  const heading = headingComponent ?? (variant === 'band' ? 'h2' : 'h3');

  const toggle = (k: ShortlistAlertEvent) =>
    setWatch((prev) => (prev.includes(k) ? prev.filter((x) => x !== k) : [...prev, k]));

  const trackStarted = (events: ShortlistAlertEvent[]) =>
    trackEvent({
      eventName: 'alert_signup_started',
      eventType: 'form',
      category: 'engagement',
      label: locationTag,
      metadata: { watch: events.join(','), place, variant, ...(analyticsSurface ? { surface: analyticsSurface } : {}) },
    });

  function advance(to: 2 | 3) {
    if (to === 2 && watch.length === 0) {
      setError('Pick at least one.');
      return;
    }
    setError(null);
    if (to === 2) trackStarted(watch);
    setStep(to);
  }

  async function send(events: ShortlistAlertEvent[], frequency: 'instant' | 'weekly') {
    if (state === 'sending') return;
    setState('sending');
    trackCtaClick('Alert Signup', locationTag, intent.name);

    const chosen: ShortlistIntent = { ...intent, alertOn: events, frequency };
    const person = contactPayload(contact);
    const consentFields = consent.payload(person.phone);

    const lead = await submitLead(
      {
        type: 'shortlist',
        antiSpam: getAntiSpamMetadata(),
        ...person,
        ...consentFields,
        answers: {
          shortlistName: chosen.name,
          frequency,
          alertOn: events.join(','),
          place,
        },
      },
      { sourcePage: locationTag, flowId: 'alert_signup' },
    ).catch(() => null);

    if (!lead?.ok) {
      setState('error');
      return;
    }

    const res = await secureFetch('/api/auth/listings-access', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({
        ...person,
        ...consentFields,
        returnUrl: typeof window === 'undefined' ? undefined : window.location.href,
        shortlist: {
          criteria: chosen.criteria,
          alertOn: chosen.alertOn,
          frequency: chosen.frequency,
          name: chosen.name,
        },
      }),
    }).catch(() => null);

    const ok = Boolean(res && res.ok);
    setState(ok ? 'sent' : 'error');
    if (ok) onSent?.();
  }

  function validEmail(): boolean {
    if (!EMAIL_RE.test(email.trim())) {
      setEmailError(ALERT_EMAIL_ERROR);
      return false;
    }
    setEmailError(null);
    return true;
  }

  function validContact(fields: readonly ContactField[]): boolean {
    const errs = validateContact(contact, t, fields);
    setContactErrors(errs);
    return Object.keys(errs).length === 0;
  }

  async function onSubmitFull(e: FormEvent) {
    e.preventDefault();
    if (!validContact(['firstName', 'lastName', 'email', 'phone'])) return;
    setError(null);
    await send(watch, cadence);
  }

  async function onSubmitRow(e: FormEvent) {
    e.preventDefault();
    if (!validEmail()) return;
    if (rowStep === 'email') {
      trackStarted(intent.alertOn);
      setRowStep('contact');
      return;
    }
    if (!validContact(['firstName', 'lastName', 'phone'])) return;
    await send(intent.alertOn, intent.frequency);
  }

  function reset() {
    setState('idle');
    setEmail('');
    setEmailError(null);
    setRowStep('email');
    setStep(1);
  }

  const surfaceSx = {
    full: { p: { xs: 4, md: 6 }, border: `1px solid ${flowColors.borderSubtle}`, backgroundColor: flowColors.surfaceCard },
    aside: { p: 3, border: `1px solid ${flowColors.borderSubtle}`, backgroundColor: flowColors.surfaceCard },
    band: { p: { xs: 4, md: 6 }, border: `1px solid ${flowColors.borderSubtle}`, backgroundColor: flowColors.surfaceCard },
    compact: { p: 0, border: 0, backgroundColor: 'transparent' },
  }[variant];

  const shellSx = {
    borderRadius: `${flowRadius.lg}px`,
    color: flowColors.textPrimary,
    ...surfaceSx,
  };

  if (state === 'sent') {
    return (
      <Box sx={shellSx} aria-live="polite">
        <Box
          sx={{
            display: 'flex',
            gap: 3,
            alignItems: 'flex-start',
            '@keyframes alertSentIn': { from: { opacity: 0, transform: 'translateY(8px)' }, to: { opacity: 1, transform: 'none' } },
            animation: 'alertSentIn 260ms cubic-bezier(0.2, 0, 0, 1)',
            [reducedMotion]: { animation: 'none' },
          }}
        >
          <Box
            sx={{
              flexShrink: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 40,
              height: 40,
              borderRadius: `${flowRadius.sm}px`,
              backgroundColor: flowColors.successSoft,
              color: flowColors.statusSuccess,
            }}
          >
            <CheckIcon />
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, minWidth: 0 }}>
            <Typography component={heading} sx={{ m: 0, fontSize: variant === 'compact' ? 17 : 20, fontWeight: 600, letterSpacing: '-0.015em' }}>
              {t('alertSignup.sent.title', { defaultValue: 'Check your email.' })}
            </Typography>
            <Typography sx={{ fontSize: 15, lineHeight: 1.6, color: flowColors.textSecondary, maxWidth: '52ch' }}>
              {t('alertSignup.sent.body', { defaultValue: 'Confirm the link to start your {{place}} alerts.', place })}
            </Typography>
            <Box
              component="button"
              type="button"
              onClick={reset}
              sx={{
                alignSelf: 'flex-start',
                minHeight: 44,
                p: 0,
                border: 0,
                background: 'none',
                font: 'inherit',
                fontSize: 13.5,
                color: flowColors.textMuted,
                textDecoration: 'underline',
                textUnderlineOffset: '3px',
                cursor: 'pointer',
                borderRadius: `${flowRadius.sm}px`,
                ...flowFocusRing,
              }}
            >
              Use a different email
            </Box>
          </Box>
        </Box>
      </Box>
    );
  }

  const sendFailed = state === 'error' ? (
    <Typography role="alert" sx={{ fontSize: 14, color: flowColors.statusError }}>
      That did not go through. Try again.
    </Typography>
  ) : null;

  if (!isFull) {
    const ctaLabel = cta ?? 'Get alerts';
    const titleText = title ?? `Get new ${place} listings by email`;
    const titleSize = variant === 'compact' ? 17 : variant === 'band' ? { xs: 22, md: 26 } : 20;

    return (
      <Box component="section" aria-labelledby={headingId} sx={{ ...shellSx, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 3 }}>
          {variant !== 'compact' ? (
            <Box
              aria-hidden="true"
              sx={{
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 40,
                height: 40,
                borderRadius: `${flowRadius.sm}px`,
                backgroundColor: flowColors.chip,
                color: flowColors.chipText,
              }}
            >
              <BellIcon />
            </Box>
          ) : null}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, minWidth: 0 }}>
            {eyebrow ? (
              <Typography sx={{ m: 0, fontSize: 11.5, fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', color: flowColors.link }}>
                {eyebrow}
              </Typography>
            ) : null}
            <Typography
              component={heading}
              id={headingId}
              sx={{ m: 0, fontSize: titleSize, fontWeight: 600, lineHeight: 1.2, letterSpacing: '-0.015em', textWrap: 'pretty' }}
            >
              {titleText}
            </Typography>
            {sub ? (
              <Typography sx={{ m: 0, fontSize: 15, lineHeight: 1.55, color: flowColors.textSecondary, textWrap: 'pretty', maxWidth: '60ch' }}>
                {sub}
              </Typography>
            ) : null}
          </Box>
        </Box>

        <Box component="form" noValidate onSubmit={onSubmitRow} sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <AntiSpamHoneypot onValueChange={setHoneypotValue} />
          <Typography component="label" htmlFor={emailId} sx={{ fontSize: 13.5, fontWeight: 500, color: flowColors.textSecondary }}>
            Email address
          </Typography>
          <Box
            sx={{
              display: 'flex',
              // Phones stack the button under the input so the placeholder is never clipped.
              flexWrap: { xs: 'wrap', sm: 'nowrap' },
              alignItems: 'stretch',
              gap: 1,
              minHeight: 56,
              // 4px inset: the 12px button sits concentric in the 16px field.
              p: 1,
              border: `1px solid ${emailError ? flowColors.statusError : flowColors.borderStrong}`,
              borderRadius: `${flowRadius.md}px`,
              backgroundColor: flowColors.surfaceInput,
              transition: flowTransition('border-color'),
              '&:focus-within': { borderColor: emailError ? flowColors.statusError : flowColors.interactivePrimary },
              [reducedMotion]: { transition: 'none' },
            }}
          >
            <Box sx={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 2, pl: 3, pr: 1, color: flowColors.textMuted }}>
              <MailIcon />
              <Box
                component="input"
                id={emailId}
                type="email"
                name="email"
                autoComplete="email"
                inputMode="email"
                required
                placeholder="name@example.com"
                value={email}
                aria-invalid={emailError ? true : undefined}
                aria-describedby={emailError ? errorId : undefined}
                onChange={(e: ChangeEvent<HTMLInputElement>) => {
                  setEmail(e.target.value);
                  if (emailError) setEmailError(null);
                }}
                sx={{
                  flex: 1,
                  minWidth: 0,
                  height: 42,
                  border: 0,
                  outline: 'none',
                  background: 'transparent',
                  color: flowColors.textPrimary,
                  font: 'inherit',
                  fontSize: 16,
                  '&::placeholder': { color: flowColors.textMuted, opacity: 1 },
                }}
              />
            </Box>
            {rowStep === 'email' ? (
              <Button
                type="submit"
                variant="contained"
                sx={{ ...flowPrimaryButtonSx, minHeight: { xs: 48, sm: 0 }, flexShrink: 0, flexBasis: { xs: '100%', sm: 'auto' }, px: { xs: 4, sm: 5 }, fontSize: 15, borderRadius: `${flowRadius.sm}px` }}
              >
                {t('forms.contact.continue', { defaultValue: 'Continue' })}
              </Button>
            ) : null}
          </Box>
          {emailError ? (
            <Typography id={errorId} role="alert" sx={{ fontSize: 13.5, color: flowColors.statusError }}>
              {emailError}
            </Typography>
          ) : null}
          {rowStep === 'contact' ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1, ...contactFieldSx }}>
              <ContactFields
                values={contact}
                errors={contactErrors}
                onChange={onContactChange}
                hideEmail
                disabled={state === 'sending'}
                idPrefix={`${emailId}-contact`}
              />
              <ConsentChoices phone values={consent.values} onChange={consent.setValues} disabled={state === 'sending'} />
              <Button
                type="submit"
                variant="contained"
                fullWidth
                disabled={state === 'sending'}
                sx={{ ...flowPrimaryButtonSx, fontSize: 15 }}
              >
                {state === 'sending' ? 'Sending…' : ctaLabel}
              </Button>
              <FormConsentLine />
            </Box>
          ) : null}
          {sendFailed}
        </Box>

        {variant === 'band' && perks && perks.length > 0 ? (
          <Box
            component="ul"
            sx={{
              listStyle: 'none',
              m: 0,
              p: 0,
              pt: 2,
              borderTop: `1px solid ${flowColors.divider}`,
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 220px), 1fr))',
              gap: { xs: 2, md: '16px 24px' },
            }}
          >
            {perks.map((perk) => (
              <Box component="li" key={perk.title} sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                <Typography component="span" sx={{ fontSize: 15, fontWeight: 500 }}>{perk.title}</Typography>
                <Typography component="span" sx={{ fontSize: 14, lineHeight: 1.55, color: flowColors.textSecondary }}>{perk.body}</Typography>
              </Box>
            ))}
          </Box>
        ) : null}
        {footer}
      </Box>
    );
  }

  return (
    <Box sx={{ ...shellSx, display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        <Typography sx={{ fontSize: 11.5, fontWeight: 600, color: flowColors.link, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
          {place} alerts
        </Typography>
        <FlowStepHeader canGoBack={step > 1} onBack={() => setStep(step === 3 ? 2 : 1)} current={step} total={3} />
      </Box>

      {/* Steps slide in the direction of travel: Continue enters from the
          end side, Back from the start side. */}
      <StepTransition activeKey={String(step)} order={step}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {step === 1 && (
        <>
          <Box>
            <Typography component={heading} sx={{ m: 0, fontSize: { xs: 20, md: 23 }, fontWeight: 600, letterSpacing: '-0.015em', mb: 1 }}>
              What do you want to hear about?
            </Typography>
          </Box>
          <Stack spacing={1}>
            {WATCH_OPTIONS.map((o) => {
              const on = watch.includes(o.key);
              return (
                <Box
                  key={o.key}
                  role="checkbox"
                  aria-checked={on}
                  tabIndex={0}
                  onClick={() => toggle(o.key)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggle(o.key); }
                  }}
                  sx={optionTileSx(on)}
                >
                  <Typography sx={{ fontSize: 15.5, fontWeight: on ? 600 : 500 }}>{o.label}</Typography>
                </Box>
              );
            })}
          </Stack>
          <Button onClick={() => advance(2)} variant="contained" fullWidth sx={flowPrimaryButtonSx}>
            Continue
          </Button>
        </>
      )}

      {step === 2 && (
        <>
          <Box>
            <Typography component={heading} sx={{ m: 0, fontSize: { xs: 20, md: 23 }, fontWeight: 600, letterSpacing: '-0.015em', mb: 1 }}>
              How often?
            </Typography>
          </Box>
          <Stack spacing={1} role="radiogroup" aria-label="How often">
            {CADENCE.map((o) => {
              const on = cadence === o.key;
              return (
                <Box
                  key={o.key}
                  role="radio"
                  aria-checked={on}
                  tabIndex={on ? 0 : -1}
                  onClick={() => setCadence(o.key)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setCadence(o.key); }
                    if (e.key === 'ArrowDown' || e.key === 'ArrowUp' || e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
                      e.preventDefault();
                      setCadence(o.key === 'instant' ? 'weekly' : 'instant');
                      const sibling = (e.key === 'ArrowDown' || e.key === 'ArrowRight'
                        ? e.currentTarget.nextElementSibling
                        : e.currentTarget.previousElementSibling) as HTMLElement | null;
                      sibling?.focus();
                    }
                  }}
                  sx={optionTileSx(on)}
                >
                  <Typography sx={{ fontSize: 15.5, fontWeight: on ? 600 : 500 }}>{o.label}</Typography>
                </Box>
              );
            })}
          </Stack>
          <Button onClick={() => advance(3)} variant="contained" fullWidth sx={flowPrimaryButtonSx}>
            Continue
          </Button>
        </>
      )}

      {step === 3 && (
        <Box component="form" noValidate onSubmit={onSubmitFull} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <AntiSpamHoneypot onValueChange={setHoneypotValue} />
          <Box>
            <Typography component={heading} sx={{ m: 0, fontSize: { xs: 20, md: 23 }, fontWeight: 600, letterSpacing: '-0.015em', mb: 1 }}>
              Where should they go?
            </Typography>
          </Box>
          <Box sx={contactFieldSx}>
            <ContactFields
              values={contact}
              errors={contactErrors}
              onChange={onContactChange}
              disabled={state === 'sending'}
              idPrefix={`${emailId}-full`}
            />
          </Box>
          <ConsentChoices phone values={consent.values} onChange={consent.setValues} disabled={state === 'sending'} />
          <Button
            type="submit"
            variant="contained"
            fullWidth
            disabled={state === 'sending'}
            sx={flowPrimaryButtonSx}
          >
            {state === 'sending' ? 'Setting up…' : 'Get alerts'}
          </Button>
          <FormConsentLine />
        </Box>
      )}
      </Box>
      </StepTransition>

      {error ? (
        <Typography role="alert" sx={{ fontSize: 14, color: flowColors.statusError }}>
          {error}
        </Typography>
      ) : null}
      {sendFailed}
    </Box>
  );
}
